// Renderer del Ticket FISCAL POS 80mm (Factura A/B/C con CAE de AFIP).
//
// Se usa desde:
// - lib/printTicket80.js (impresión real al cerrar venta con CAE)
// - pages/AdminLabelsPage (preview en vivo del editor)
//
// Estructura fiscal (RG 3561 / 4291 AFIP):
//   • Datos del emisor (razón social, CUIT, IIBB, cond. IVA, inicio act., domicilio)
//   • Letra grande del comprobante (A/B/C) + código (01/06/11)
//   • Punto de venta + número + fecha
//   • Datos del cliente (nombre, CUIT/DNI, cond. IVA)
//   • Items con precio unitario e importe
//   • Subtotal · IVA (discriminado en A) · Total
//   • Forma de pago + vuelto
//   • CAE + vencimiento + QR AFIP

const esc = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const money = (n) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

// Mapeo tipo_comprobante → letra + código AFIP
const LETRA_MAP = {
  "FACTURA A": { letra: "A", codigo: "01" },
  "FACTURA B": { letra: "B", codigo: "06" },
  "FACTURA C": { letra: "C", codigo: "11" },
  "NOTA DE CREDITO A": { letra: "A", codigo: "03" },
  "NOTA DE CREDITO B": { letra: "B", codigo: "08" },
  "NOTA DE CREDITO C": { letra: "C", codigo: "13" },
  "NOTA DE DEBITO A": { letra: "A", codigo: "02" },
  "NOTA DE DEBITO B": { letra: "B", codigo: "07" },
  "NOTA DE DEBITO C": { letra: "C", codigo: "12" },
};

/**
 * Datos de ejemplo para el preview del admin.
 */
export const SAMPLE_FACTURA = {
  tipo_comprobante: "FACTURA B",
  numero: "0004-00000123",
  fecha: new Date().toISOString().slice(0, 10),
  contraparte_nombre: "Juan Pérez",
  contraparte_cuit: "20-12345678-9",
  contraparte_condicion_iva: "Consumidor Final",
  items: [
    { descripcion: "Asado de tira", cantidad: 1, kilos: 2, precio_unitario: 7692.31, iva_pct: 10.5, subtotal: 15384.62 },
    { descripcion: "Chorizo criollo", cantidad: 6, kilos: 0, precio_unitario: 1085.97, iva_pct: 10.5, subtotal: 6515.83 },
  ],
  iva: 2308.85,
  iva_pct: 10.5,
  total: 24209.30,
  cae: "74123456789012",
  cae_vencimiento: "2026-03-15",
  afip_qr_url: "https://www.afip.gob.ar/fe/qr/?p=eyJ2ZXIiOjEsImZlY2hhIjoiMjAyNi0wMi0xNSJ9",
  metodo_pago: "Efectivo",
  vuelto: 100,
};

/**
 * @param {object} factura — documento de venta con CAE
 * @param {object} config — objeto con textos/secciones/fuentes/layout resuelto
 * @param {{autoprint?: boolean, logo?: string, empresa?: object, preview?: boolean}} opts
 */
export function renderPosTicketFiscal80Html(factura, config, opts = {}) {
  const { autoprint = false, logo = "", empresa = {}, preview = false } = opts;
  const textos = config?.textos || {};
  const sec = config?.secciones || {};
  const fnt = config?.fuentes || {};
  const layout = config?.layout || {};
  const anchoMm = layout.ancho_mm ?? 72;
  const margenMm = layout.margen_mm ?? 2;
  const qrMm = layout.qr_mm ?? 22;

  const tipoUp = (factura.tipo_comprobante || "FACTURA B").toUpperCase();
  const letraInfo = LETRA_MAP[tipoUp] || { letra: "X", codigo: "" };

  // Fecha del comprobante (si viene en factura.fecha "YYYY-MM-DD") o ahora
  let fechaTxt = "";
  try {
    const d = factura.fecha ? new Date(factura.fecha + "T12:00:00") : new Date();
    fechaTxt = d.toLocaleString("es-AR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch (_) {
    fechaTxt = new Date().toLocaleDateString("es-AR");
  }
  const horaTxt = new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });

  // Items — calcular precio unitario con IVA y subtotal con IVA para mostrar al cliente
  const items = (factura.items || []).map((it) => {
    const qty = Number(it.kilos && Number(it.kilos) > 0 ? it.kilos : (it.cantidad || 1));
    const puNeto = Number(it.precio_unitario || 0);
    const ivaPct = Number(it.iva_pct || 0);
    const puCIva = puNeto * (1 + ivaPct / 100);
    const subCIva = puCIva * qty;
    return {
      qty,
      unidad: it.kilos && Number(it.kilos) > 0 ? "kg" : "u",
      desc: it.descripcion || "",
      puCIva,
      subCIva,
    };
  });

  // Totales
  const totalGeneral = Number(factura.total || 0);
  const ivaTotal = Number(factura.iva || 0);
  const neto = totalGeneral - ivaTotal;
  const showIvaDiscr = letraInfo.letra === "A" && sec.iva_discriminado !== false;

  // Punto de venta y número separados (si el número viene formateado "0004-00000123")
  const numTxt = factura.numero || "";
  let pvTxt = "", nroTxt = numTxt;
  if (numTxt.includes("-")) {
    const parts = numTxt.split("-");
    pvTxt = parts[0];
    nroTxt = parts[1];
  } else if (factura.afip_punto_venta) {
    pvTxt = String(factura.afip_punto_venta).padStart(4, "0");
    nroTxt = String(factura.afip_numero || "").padStart(8, "0");
  }

  // Empresa (emisor)
  const emp = empresa || {};
  const showLogo = sec.logo !== false && !!logo;
  const showEmisorRs = sec.emisor_razon_social !== false && emp.razon_social;
  const showEmisorCuit = sec.emisor_cuit !== false && emp.cuit;
  const showEmisorCond = sec.emisor_condicion !== false && emp.condicion_iva;
  const showEmisorIb = sec.emisor_ib !== false && emp.ingresos_brutos;
  const showEmisorIA = sec.emisor_inicio_act !== false && emp.inicio_actividades;
  const showEmisorDom = sec.emisor_domicilio !== false && emp.domicilio;
  const showLetra = sec.letra_comprobante !== false;
  const showTipo = sec.tipo_comprobante !== false;
  const showPvNro = sec.punto_venta_numero !== false;
  const showFecha = sec.fecha_cabecera !== false;
  const showCteNombre = sec.cliente_nombre !== false;
  const showCteCuit = sec.cuit_cliente !== false && factura.contraparte_cuit;
  const showCteCond = sec.condicion_cliente !== false && factura.contraparte_condicion_iva;
  const showColPu = sec.columna_pu !== false;
  const showColImp = sec.columna_importe !== false;
  const showSubtotal = sec.subtotal !== false;
  const showTotal = sec.total !== false;
  const showPago = sec.metodo_pago !== false && factura.metodo_pago;
  const showVuelto = sec.vuelto !== false && Number(factura.vuelto || 0) > 0;
  const showCae = sec.cae !== false && factura.cae;
  const showCaeVenc = sec.cae_vencimiento !== false && factura.cae_vencimiento;
  const showQr = sec.qr_afip !== false && factura.afip_qr_url;
  const showFooter = sec.footer !== false && (textos.footer || "");

  // QR: usamos api.qrserver.com para generar la imagen del URL AFIP (sin backend extra)
  const qrPx = Math.round(qrMm * 8); // ~8 px/mm a 203dpi
  const qrSrc = showQr ? `https://api.qrserver.com/v1/create-qr-code/?size=${qrPx}x${qrPx}&margin=0&data=${encodeURIComponent(factura.afip_qr_url)}` : "";

  const style = `
    @page { size: ${anchoMm + margenMm * 2}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      width: ${anchoMm}mm;
      margin: 0;
      padding: ${margenMm}mm ${margenMm}mm ${margenMm * 2}mm ${margenMm}mm;
      font-family: 'Helvetica', Arial, sans-serif;
      font-size: ${fnt.base_px ?? 12}px;
      font-weight: 400;
      line-height: 1.25;
      color: #000;
      ${preview ? "background: #fff;" : ""}
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: 700; }
    .divider { border: none; border-top: 2px solid #000; margin: 4px 0; }
    .divider-dash { border: none; border-top: 1px dashed #000; margin: 3px 0; }
    .logo { max-width: ${anchoMm - 20}mm; max-height: 16mm; margin: 0 auto 3px; display: block; filter: grayscale(1) contrast(1.8) brightness(0.85); }
    .titulo { font-size: ${fnt.titulo_px ?? 14}px; text-align: center; }
    .emisor { font-size: ${fnt.emisor_px ?? 12}px; text-align: center; }
    .cliente { font-size: ${fnt.cliente_px ?? 13}px; }
    .letra-box {
      display: flex; align-items: center; justify-content: center;
      border: 3px solid #000; width: 14mm; height: 14mm;
      margin: 2px auto 0;
      font-size: ${fnt.letra_px ?? 40}px; font-weight: 700; line-height: 1;
    }
    .tipo-cbte { text-align: center; font-size: ${fnt.titulo_px ?? 14}px; margin-top: 2px; }
    .cod-cbte { text-align: center; font-size: ${(fnt.emisor_px ?? 12) - 1}px; }
    .pv-row { display: flex; justify-content: space-between; gap: 6px; margin-top: 3px; font-size: ${fnt.cliente_px ?? 13}px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 2px 0; vertical-align: top; }
    th { border-bottom: 2px solid #000; text-align: left; font-size: ${(fnt.items_px ?? 13) - 1}px; font-weight: 700; }
    th.n, td.n { text-align: right; }
    .items td { font-size: ${fnt.items_px ?? 13}px; font-weight: 400; padding: 3px 0; border-bottom: 1px dashed #000; }
    .items td.qty { width: 12mm; text-align: right; padding-right: 2mm; white-space: nowrap; }
    .items td.pu { width: 18mm; text-align: right; padding-right: 2mm; white-space: nowrap; font-family: monospace; }
    .items td.imp { width: 20mm; text-align: right; white-space: nowrap; font-family: monospace; }
    .items td.desc { word-wrap: break-word; overflow-wrap: anywhere; text-transform: uppercase; }
    .totals td { padding: 2px 0; font-size: ${fnt.totales_px ?? 14}px; font-weight: 400; }
    .totals .lbl { text-align: right; padding-right: 6px; }
    .totals .val { text-align: right; font-family: monospace; }
    .total-final { font-size: ${fnt.total_final_px ?? 20}px; }
    .box { border: 2px solid #000; padding: 3px 5px; margin-top: 3px; border-radius: 2px; font-weight: 400; }
    .cae-row { font-size: ${fnt.cae_px ?? 12}px; text-align: center; margin-top: 2px; }
    .cae-nro { font-family: monospace; letter-spacing: 0.5px; }
    .qr-wrap { text-align: center; margin-top: 4px; }
    .qr-wrap img { width: ${qrMm}mm; height: ${qrMm}mm; display: inline-block; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const script = autoprint ? `
    <script>
      window.addEventListener('load', function () {
        setTimeout(function () { window.focus(); window.print(); }, 150);
      });
    </script>
  ` : "";

  const emisorBlock = `
    ${showLogo ? `<img class="logo" src="${esc(logo)}" alt="logo" />` : ""}
    ${showEmisorRs ? `<div class="emisor bold">${esc(emp.razon_social)}</div>` : ""}
    ${showEmisorCond ? `<div class="emisor">${esc(emp.condicion_iva)}</div>` : ""}
    ${showEmisorCuit ? `<div class="emisor">CUIT: ${esc(emp.cuit)}</div>` : ""}
    ${showEmisorIb ? `<div class="emisor">IIBB: ${esc(emp.ingresos_brutos)}</div>` : ""}
    ${showEmisorIA ? `<div class="emisor">Inicio Act.: ${esc(emp.inicio_actividades)}</div>` : ""}
    ${showEmisorDom ? `<div class="emisor">${esc(emp.domicilio)}</div>` : ""}
  `;

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>${esc(tipoUp)} ${esc(numTxt)}</title>
<style>${style}</style>
</head>
<body>
  ${emisorBlock}
  <hr class="divider" />
  ${showLetra ? `<div class="letra-box">${esc(letraInfo.letra)}</div>` : ""}
  ${showLetra && letraInfo.codigo ? `<div class="cod-cbte">COD. ${esc(letraInfo.codigo)}</div>` : ""}
  ${showTipo ? `<div class="tipo-cbte bold">${esc(tipoUp)}</div>` : ""}
  ${showPvNro ? `
    <div class="pv-row">
      <div>${esc(textos.label_pv || "Pto. Vta.:")} <span class="bold">${esc(pvTxt || "-")}</span></div>
      <div>${esc(textos.label_nro || "Nº:")} <span class="bold">${esc(nroTxt || "-")}</span></div>
    </div>
  ` : ""}
  ${showFecha ? `<div class="pv-row"><div>${esc(textos.label_fecha || "Fecha:")} <span class="bold">${esc(fechaTxt)} ${esc(horaTxt)}</span></div></div>` : ""}
  <hr class="divider" />
  ${showCteNombre ? `<div class="cliente"><span class="bold">${esc(textos.label_cliente || "Cliente:")}</span> ${esc(factura.contraparte_nombre || "Consumidor Final")}</div>` : ""}
  ${showCteCuit ? `<div>${esc(textos.label_cuit || "CUIT/DNI:")} ${esc(factura.contraparte_cuit)}</div>` : ""}
  ${showCteCond ? `<div>${esc(textos.label_cond || "Cond. IVA:")} ${esc(factura.contraparte_condicion_iva)}</div>` : ""}
  <hr class="divider" />
  <table class="items">
    <thead>
      <tr>
        <th class="n" style="width:12mm">${esc(textos.col_cantidad || "Cant.")}</th>
        <th>${esc(textos.col_detalle || "Detalle")}</th>
        ${showColPu ? `<th class="n">${esc(textos.col_pu || "P.U.")}</th>` : ""}
        ${showColImp ? `<th class="n">${esc(textos.col_importe || "Importe")}</th>` : ""}
      </tr>
    </thead>
    <tbody>
      ${items.map((it) => `
        <tr>
          <td class="qty">${esc(String(it.qty))}${it.unidad ? ` ${esc(it.unidad)}` : ""}</td>
          <td class="desc">${esc(it.desc)}</td>
          ${showColPu ? `<td class="pu">${esc(money(it.puCIva))}</td>` : ""}
          ${showColImp ? `<td class="imp">${esc(money(it.subCIva))}</td>` : ""}
        </tr>
      `).join("")}
    </tbody>
  </table>
  <hr class="divider" />
  <table class="totals">
    ${showIvaDiscr ? `
      <tr><td class="lbl">${esc(textos.label_neto || "Neto Gravado:")}</td><td class="val">${esc(money(neto))}</td></tr>
      <tr><td class="lbl">${esc(textos.label_iva || "IVA:")}</td><td class="val">${esc(money(ivaTotal))}</td></tr>
    ` : (showSubtotal && Math.abs(neto - totalGeneral) > 0.01 ? `
      <tr><td class="lbl">${esc(textos.label_subtotal || "Subtotal:")}</td><td class="val">${esc(money(neto))}</td></tr>
    ` : "")}
    ${showTotal ? `<tr><td class="lbl bold total-final">${esc(textos.label_total || "TOTAL:")}</td><td class="val bold total-final">${esc(money(totalGeneral))}</td></tr>` : ""}
  </table>
  ${showPago ? `<div class="box"><span class="bold">${esc(textos.label_pago || "Forma de Pago:")}</span> ${esc(factura.metodo_pago)}</div>` : ""}
  ${showVuelto ? `<div class="box"><span class="bold">${esc(textos.label_vuelto || "Vuelto:")}</span> ${esc(money(factura.vuelto))}</div>` : ""}
  <hr class="divider" />
  ${showCae ? `<div class="cae-row">${esc(textos.label_cae || "CAE Nº:")} <span class="cae-nro bold">${esc(factura.cae)}</span></div>` : ""}
  ${showCaeVenc ? `<div class="cae-row">${esc(textos.label_cae_venc || "Vto. CAE:")} <span class="bold">${esc(factura.cae_vencimiento)}</span></div>` : ""}
  ${showQr ? `<div class="qr-wrap"><img src="${esc(qrSrc)}" alt="QR AFIP" /></div>` : ""}
  ${showFooter ? `<hr class="divider-dash" /><div class="center">${esc(textos.footer || "¡Gracias por su compra!")}</div>` : ""}
  <div style="height: 6mm"></div>
  ${script}
</body></html>`;
}
